import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Calendar, Eye, ArrowLeft, Bookmark, BookmarkCheck, Linkedin, Twitter, Facebook, Link as LinkIcon } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [isSaved, setIsSaved] = useState(false);

  useEffect(() => {
    const fetchPost = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${API}/posts/${slug}`);
        setPost(response.data);
        
        // Fetch related posts
        const relatedRes = await axios.get(`${API}/posts?category=${encodeURIComponent(response.data.category)}&limit=3`);
        setRelatedPosts(relatedRes.data.filter(p => p.slug !== slug).slice(0, 2));
        
        // Check if saved
        if (user) {
          setIsSaved(user.saved_articles?.includes(response.data.post_id));
        }
      } catch (error) {
        console.error('Error fetching post:', error);
        navigate('/blog');
      } finally {
        setLoading(false);
      }
    };
    fetchPost();
  }, [slug, navigate, user]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleSaveArticle = async () => {
    if (!user) {
      toast.error('Please sign in to save articles');
      return;
    }

    try {
      if (isSaved) {
        await axios.delete(`${API}/user/save-article/${post.post_id}`, { withCredentials: true });
        setIsSaved(false);
        toast.success('Article removed from saved');
      } else {
        await axios.post(`${API}/user/save-article/${post.post_id}`, {}, { withCredentials: true });
        setIsSaved(true);
        toast.success('Article saved!');
      }
    } catch (error) {
      toast.error('Failed to update saved articles');
    }
  };

  const sharePost = (platform) => {
    const url = window.location.href;
    const text = post.title;
    
    const shareUrls = {
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`
    };
    
    window.open(shareUrls[platform], '_blank', 'width=600,height=400');
  };

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success('Link copied to clipboard!');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-accent border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-secondary text-lg mb-4">Post not found</p>
          <Link to="/blog">
            <Button className="btn-primary rounded-none">
              Back to Blog
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="blog-post-page">
      {/* Hero */}
      <section className="bg-white py-8 border-b border-primary/10">
        <div className="container-padding">
          <Link 
            to="/blog"
            className="inline-flex items-center gap-2 text-secondary hover:text-primary transition-colors mb-8"
            data-testid="back-to-blog"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="font-sans text-sm">Back to Blog</span>
          </Link>
          
          <div className="max-w-4xl">
            <div className="flex items-center gap-4 mb-4">
              <span className="bg-accent text-white px-3 py-1 text-xs font-sans font-medium">
                {post.category}
              </span>
              <span className="text-secondary text-sm flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {formatDate(post.created_at)}
              </span>
              <span className="text-secondary text-sm flex items-center gap-1">
                <Eye className="w-4 h-4" />
                {post.views} views
              </span>
            </div>
            
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-light tracking-tight text-primary mb-6">
              {post.title}
            </h1>
            
            <p className="text-xl text-secondary font-serif leading-relaxed mb-8">
              {post.excerpt}
            </p>
            
            <div className="flex items-center justify-between">
              <p className="text-sm text-secondary font-sans">
                By <span className="text-primary font-medium">{post.author}</span>
              </p>
              
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleSaveArticle}
                  className="flex items-center gap-2"
                  data-testid="save-article-btn"
                >
                  {isSaved ? (
                    <BookmarkCheck className="w-5 h-5 text-accent" />
                  ) : (
                    <Bookmark className="w-5 h-5" />
                  )}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => sharePost('linkedin')}
                  data-testid="share-linkedin"
                >
                  <Linkedin className="w-5 h-5" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => sharePost('twitter')}
                  data-testid="share-twitter"
                >
                  <Twitter className="w-5 h-5" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => sharePost('facebook')}
                  data-testid="share-facebook"
                >
                  <Facebook className="w-5 h-5" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={copyLink}
                  data-testid="copy-link"
                >
                  <LinkIcon className="w-5 h-5" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Image */}
      {post.featured_image && (
        <section className="bg-white pb-8">
          <div className="container-padding">
            <div className="max-w-4xl">
              <div className="aspect-[21/9] overflow-hidden border border-primary/10">
                <img 
                  src={post.featured_image}
                  alt={post.title}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Content */}
      <section className="section-padding bg-white">
        <div className="container-padding">
          <div className="max-w-3xl mx-auto">
            <div 
              className="blog-content"
              dangerouslySetInnerHTML={{ __html: post.content }}
              data-testid="blog-content"
            />
          </div>
        </div>
      </section>

      {/* Share Bar */}
      <section className="py-8 bg-background border-y border-primary/10">
        <div className="container-padding">
          <div className="max-w-3xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-secondary font-sans text-sm">Share this article:</p>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => sharePost('linkedin')}
                className="flex items-center gap-2 rounded-none"
              >
                <Linkedin className="w-4 h-4" />
                LinkedIn
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => sharePost('twitter')}
                className="flex items-center gap-2 rounded-none"
              >
                <Twitter className="w-4 h-4" />
                Twitter
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => sharePost('facebook')}
                className="flex items-center gap-2 rounded-none"
              >
                <Facebook className="w-4 h-4" />
                Facebook
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <section className="section-padding">
          <div className="container-padding">
            <div className="max-w-4xl mx-auto">
              <h3 className="text-2xl font-sans font-normal text-primary mb-8">Related Articles</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {relatedPosts.map((relatedPost) => (
                  <Link 
                    key={relatedPost.post_id}
                    to={`/blog/${relatedPost.slug}`}
                    className="group"
                  >
                    <div className="aspect-[16/9] overflow-hidden border border-primary/10 mb-4">
                      <img 
                        src={relatedPost.featured_image || 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800'}
                        alt={relatedPost.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <p className="label-uppercase mb-2">{relatedPost.category}</p>
                    <h4 className="font-sans text-lg font-medium text-primary group-hover:text-accent transition-colors">
                      {relatedPost.title}
                    </h4>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default BlogPost;
